import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useOmniAgentActions } from '../../context/OmniAgentContext';
import { unitsOf, wordChanges, resolveSelection, outcomeNote } from './changeRows';
import './DiffCard.css';

// The one way a person decides on proposed resume changes: a tick per change,
// the old words under the new ones, edit in place, then apply what is ticked
// (founder, 2026-09-27: "I have no way to just approve selected one"). The
// chat's card (DiffCard) and the Resume panel's review page (ReviewPage) both
// draw it from here, so the two can never disagree about what a tick means.
// The rows and what a selection writes live in changeRows.js.

export const Check = () => (
  <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
    <path d="M3.5 8.5l3 3 6-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Parts = ({ parts, Tag }) => parts.map((p, i) => (p.changed ? <Tag key={i}>{p.text}</Tag> : <React.Fragment key={i}>{p.text}</React.Fragment>));

export const short = (s = '', n = 60) => (s.length > n ? `${s.slice(0, n - 1).trimEnd()}…` : s);

// A textarea that grows with its words, so editing a line looks like the line.
const LineEditor = ({ value, onDone, onCancel, label }) => {
  const [text, setText] = useState(value);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [text]);
  return (
    <textarea
      ref={ref}
      className="ag-cc-input"
      value={text}
      rows={1}
      aria-label={label}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => onDone(text)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); onDone(text); }
      }}
    />
  );
};

/**
 * The state of one review over a group of proposals: which rows are ticked,
 * what the person typed over, and what applying or discarding did.
 */
export function useChangeReview(group = []) {
  const { acceptChanges, rejectChanges, dismissChanges } = useOmniAgentActions();
  const units = useMemo(() => unitsOf(group), [group]);
  const [off, setOff] = useState({}); // row key → true when unticked
  const [edits, setEdits] = useState({}); // row key → the person's words
  const [editing, setEditing] = useState(null);
  const [openSame, setOpenSame] = useState({});
  const [phase, setPhase] = useState('idle'); // idle | applying | failed
  const [done, setDone] = useState(null); // what the last action did, kept after the proposals leave

  const rows = units.flatMap(u => u.rows);
  const rowByKey = new Map(rows.map(r => [r.key, r]));
  const isOn = (key) => !off[key];
  const editOf = (key) => edits[key];
  const kept = rows.filter(r => isOn(r.key)).length;
  const total = rows.length;

  // The fact check's notes, each under the line it is about when one says it.
  const noteRow = new Map();
  const looseNotes = [];
  const seen = new Set();
  group.forEach(c => (Array.isArray(c.warnings) ? c.warnings : []).forEach(w => {
    const fact = String(w?.fact || '').toLowerCase();
    if (!fact || seen.has(fact)) return;
    seen.add(fact);
    const at = rows.find(r => String(editOf(r.key) || r.newText || '').toLowerCase().includes(fact));
    if (at) noteRow.set(at.key, [...(noteRow.get(at.key) || []), w]); else looseNotes.push(w);
  }));

  const reset = () => { setOff({}); setEdits({}); setEditing(null); };
  const apply = async () => {
    if (!kept || phase === 'applying') return;
    const plan = resolveSelection(group, isOn, editOf);
    setPhase('applying');
    if (plan.drop.length && dismissChanges) dismissChanges(plan.drop);
    const ok = plan.apply.length ? await acceptChanges(plan.apply, { note: outcomeNote(plan) }) : true;
    setPhase(ok ? 'idle' : 'failed');
    if (ok) { setDone({ kind: 'applied', kept: plan.kept, total: plan.total }); reset(); }
  };
  const discard = () => {
    rejectChanges(group);
    setDone({ kind: 'discarded', total });
    reset();
  };

  return {
    group, units, rows, rowByKey, isOn, editOf, kept, total, noteRow, looseNotes,
    editing, phase, done, apply, discard,
    startEdit: (key) => setEditing(key),
    cancelEdit: () => setEditing(null),
    commitEdit: (key, text) => {
      setEditing(null);
      const clean = String(text || '').trim();
      if (!clean) return;
      setEdits(prev => ({ ...prev, [key]: clean }));
      setOff(prev => ({ ...prev, [key]: false }));
    },
    toggle: (key) => setOff(prev => ({ ...prev, [key]: !prev[key] })),
    setAll: (on) => setOff(on ? {} : Object.fromEntries(rows.map(r => [r.key, true]))),
    isSameOpen: (unitKey) => Boolean(openSame[unitKey]),
    toggleSame: (unitKey) => setOpenSame(prev => ({ ...prev, [unitKey]: !prev[unitKey] })),
  };
}

const Note = ({ w }) => (
  <p className="ag-cc-note">
    <span className="material-symbols-rounded" aria-hidden="true">info</span>
    <span><strong>{w.fact}</strong> {w.note || 'check this is true'}</span>
  </p>
);

export const LooseNotes = ({ review }) => (review.looseNotes.length > 0 ? (
  <div className="ag-cc-notes">{review.looseNotes.map((w, i) => <Note key={i} w={w} />)}</div>
) : null);

/** One change with its tick: the new words, what they replace, edit on hover. */
export function ChangeRow({ review, row }) {
  const on = review.isOn(row.key);
  const typed = review.editOf(row.key);
  const now = typeof typed === 'string' ? typed : row.newText;
  const diff = row.kind === 'modified' || row.kind === 'field' ? wordChanges(row.oldText || '', now || '') : null;
  const notes = review.noteRow.get(row.key) || [];
  const words = row.newText || row.oldText || '';
  const editing = review.editing === row.key;
  return (
    <li className={`ag-cc-row is-${row.kind}${on ? '' : ' is-off'}`}>
      <label className="ag-cc-check">
        <input type="checkbox" checked={on} onChange={() => review.toggle(row.key)} aria-label={`Use this change: ${short(words)}`} />
        <span className="ag-cc-box" aria-hidden="true"><Check /></span>
      </label>

      <div className="ag-cc-text">
        {editing ? (
          <LineEditor value={now || ''} label="Edit this line" onCancel={review.cancelEdit} onDone={(text) => review.commitEdit(row.key, text)} />
        ) : row.kind === 'removed' || row.kind === 'entry-delete' ? (
          <p className="ag-cc-new">
            <span className="ag-cc-label">{row.label}</span>{' '}
            <span className="ag-cc-gone">{row.oldText}</span>
          </p>
        ) : (
          <p className="ag-cc-new">
            {row.label && <><span className="ag-cc-label">{row.label}</span>{' '}</>}
            {diff ? <Parts parts={diff.newParts} Tag="mark" /> : now}
          </p>
        )}
        {row.kind === 'entry-add' && row.lines?.length > 0 && (
          <ul className="ag-cc-lines">{row.lines.map((l, i) => <li key={i}>{l}</li>)}</ul>
        )}
        {diff && row.oldText && !editing && (
          <p className="ag-cc-was">
            <span className="ag-cc-was-tag">Was</span>{' '}
            <Parts parts={diff.oldParts} Tag="del" />
          </p>
        )}
        {notes.map((w, i) => <Note key={i} w={w} />)}
      </div>

      {row.editable && !editing && (
        <button type="button" className="ag-cc-icon ag-cc-edit" onClick={() => review.startEdit(row.key)} aria-label={`Edit: ${short(words)}`} title="Edit">
          <span className="material-symbols-rounded" aria-hidden="true">edit</span>
        </button>
      )}
    </li>
  );
}

/** What the last action did, in one quiet line. */
export function ReviewStatus({ review }) {
  if (review.phase === 'applying') {
    return <div className="ag-cc-status" role="status"><span className="ag-cc-spinner" aria-hidden="true" />Applying…</div>;
  }
  if (review.phase === 'failed') {
    return (
      <div className="ag-cc-status is-error" role="alert">
        <span className="material-symbols-rounded" aria-hidden="true">error</span>
        Couldn&apos;t save. Nothing else changed.
        <button type="button" className="ag-cc-link" onClick={review.apply}>Try again</button>
      </div>
    );
  }
  const { done } = review;
  if (!done) return null;
  if (done.kind === 'discarded') {
    return (
      <div className="ag-cc-status" role="status">
        <span className="material-symbols-rounded" aria-hidden="true">close</span>
        Discarded
      </div>
    );
  }
  const text = done.total > 1 && done.kept < done.total
    ? `Applied ${done.kept} of ${done.total} changes`
    : done.kept > 1 ? `Applied ${done.kept} changes` : 'Applied';
  return (
    <div className="ag-cc-status is-done" role="status">
      <span className="material-symbols-rounded" aria-hidden="true">check</span>
      {text}
    </div>
  );
}

/** The one decision: how many are ticked, and apply them. */
export function ReviewActions({ review, className = 'ag-cc-foot' }) {
  const { kept, total } = review;
  const applyLabel = total <= 1 || kept === 0 ? 'Apply' : kept === total ? 'Apply all' : `Apply ${kept}`;
  return (
    <div className={className}>
      <span className="ag-cc-count">{total > 1 ? `${kept} of ${total} selected` : ''}</span>
      {total > 1 && (
        <button type="button" className="ag-cc-link" onClick={() => review.setAll(kept < total)}>
          {kept < total ? 'Select all' : 'Clear'}
        </button>
      )}
      <div className="ag-cc-actions">
        <button type="button" className="ag-cc-btn is-quiet" onClick={review.discard}>Discard</button>
        <button type="button" className="ag-cc-btn is-primary" onClick={review.apply} disabled={!kept}>{applyLabel}</button>
      </div>
    </div>
  );
}
